/**
 * Blog Admin Page
 *
 * Superadmin page for managing blog posts (create, edit, delete).
 * Simple interface focused on SEO content management.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listBlogPostsAdmin,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  type BlogPost,
} from '../../lib/api';
import RichTextEditor from '../../components/blog/RichTextEditor';

type EditorMode = 'list' | 'create' | 'edit';

interface PostForm {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string;
  category: string;
  tags: string;
  status: 'draft' | 'published';
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
}

const initialForm: PostForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  featured_image: '',
  category: 'general',
  tags: '',
  status: 'draft',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
};

export default function BlogAdminPage() {
  const { t, i18n } = useTranslation('blog');
  const isRTL = i18n.dir() === 'rtl';

  const CATEGORIES = [
    { value: 'general', label: t('admin.categories.general') },
    { value: 'education', label: t('admin.categories.education') },
    { value: 'business', label: t('admin.categories.business') },
    { value: 'tips', label: t('admin.categories.tips') },
    { value: 'announcements', label: t('admin.categories.announcements') },
  ];

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<EditorMode>('list');
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [form, setForm] = useState<PostForm>(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  useEffect(() => {
    loadPosts();
  }, [statusFilter, pagination.page]);

  async function loadPosts() {
    try {
      setLoading(true);
      const response = await listBlogPostsAdmin({
        page: pagination.page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setPosts(response.posts);
      setPagination({
        page: response.pagination.page,
        totalPages: response.pagination.totalPages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setForm(initialForm);
    setEditingSlug(null);
    setMode('create');
    setError(null);
    setSuccess(null);
  }

  function handleEdit(post: BlogPost) {
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content,
      featured_image: post.featured_image || '',
      category: post.category,
      tags: post.tags?.join(', ') || '',
      status: post.status as 'draft' | 'published',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
    });
    setEditingSlug(post.slug);
    setMode('edit');
    setError(null);
    setSuccess(null);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.content.trim()) {
      setError(t('admin.messages.titleContentRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      if (mode === 'create') {
        await createBlogPost({
          title: form.title,
          content: form.content,
          slug: form.slug || undefined,
          excerpt: form.excerpt || undefined,
          featured_image: form.featured_image || undefined,
          category: form.category,
          tags: tags.length > 0 ? tags : undefined,
          status: form.status,
          meta_title: form.meta_title || undefined,
          meta_description: form.meta_description || undefined,
          meta_keywords: form.meta_keywords || undefined,
        });
        setSuccess(t('admin.messages.postCreated'));
      } else if (editingSlug) {
        await updateBlogPost(editingSlug, {
          title: form.title,
          content: form.content,
          slug: form.slug !== editingSlug ? form.slug : undefined,
          excerpt: form.excerpt || undefined,
          featured_image: form.featured_image || undefined,
          category: form.category,
          tags: tags,
          status: form.status,
          meta_title: form.meta_title || undefined,
          meta_description: form.meta_description || undefined,
          meta_keywords: form.meta_keywords || undefined,
        });
        setSuccess(t('admin.messages.postUpdated'));
      }

      await loadPosts();
      setTimeout(() => {
        setMode('list');
        setSuccess(null);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.messages.failedToSave'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(slug: string) {
    if (!confirm(t('admin.messages.confirmDelete'))) {
      return;
    }

    try {
      await deleteBlogPost(slug);
      await loadPosts();
      setSuccess(t('admin.messages.postDeleted'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.messages.failedToDelete'));
    }
  }

  function generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
      .substring(0, 100);
  }

  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {mode === 'create' ? t('admin.editor.createTitle') : t('admin.editor.editTitle')}
          </h1>
          <button
            onClick={() => setMode('list')}
            className="text-gray-600 hover:text-gray-900"
          >
            {t('admin.editor.cancel')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        <div className="space-y-6 bg-white rounded-xl shadow-sm p-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.editor.titleRequired')}
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => {
                setForm({ ...form, title: e.target.value });
                if (mode === 'create' && !form.slug) {
                  setForm((f) => ({ ...f, slug: generateSlug(e.target.value) }));
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('admin.editor.enterPostTitle')}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.editor.slug')}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-sm">{t('admin.editor.slugPrefix')}</span>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('admin.editor.slugPlaceholder')}
              />
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.editor.excerpt')}
            </label>
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('admin.editor.excerptPlaceholder')}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.editor.content')}
            </label>
            <RichTextEditor
              content={form.content}
              onChange={(html) => setForm({ ...form, content: html })}
              placeholder={t('admin.editor.contentPlaceholder')}
            />
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.editor.featuredImage')}
            </label>
            <input
              type="url"
              value={form.featured_image}
              onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('admin.editor.featuredImagePlaceholder')}
            />
          </div>

          {/* Category & Status */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.editor.category')}
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.editor.status')}
              </label>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as 'draft' | 'published' })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="draft">{t('admin.status.draft')}</option>
                <option value="published">{t('admin.status.published')}</option>
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.editor.tags')}
            </label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('admin.editor.tagsPlaceholder')}
            />
          </div>

          {/* SEO Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.editor.seoSettings')}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.editor.metaTitle')}
                </label>
                <input
                  type="text"
                  value={form.meta_title}
                  onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('admin.editor.metaTitlePlaceholder')}
                  maxLength={60}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('admin.editor.metaTitleLength', { count: form.meta_title.length })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.editor.metaDescription')}
                </label>
                <textarea
                  value={form.meta_description}
                  onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('admin.editor.metaDescriptionPlaceholder')}
                  maxLength={160}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('admin.editor.metaDescriptionLength', { count: form.meta_description.length })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.editor.metaKeywords')}
                </label>
                <input
                  type="text"
                  value={form.meta_keywords}
                  onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('admin.editor.metaKeywordsPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              onClick={() => setMode('list')}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              {t('admin.editor.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('admin.editor.saving') : mode === 'create' ? t('admin.editor.createPost') : t('admin.editor.updatePost')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.title')}</h1>
          <p className="text-gray-600 text-sm mt-1">
            {t('admin.subtitle')}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.newPost')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('admin.allStatuses')}</option>
          <option value="draft">{t('admin.status.draft')}</option>
          <option value="published">{t('admin.status.published')}</option>
          <option value="archived">{t('admin.status.archived')}</option>
        </select>
        <Link
          to="/blog"
          target="_blank"
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          {t('admin.viewPublicBlog')}
        </Link>
      </div>

      {/* Posts Table */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
          {t('admin.loading')}
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.noPosts')}</h2>
          <p className="text-gray-500 mb-4">{t('admin.noPostsDescription')}</p>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            {t('admin.createFirstPost')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('admin.table.post')}
                </th>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('admin.table.category')}
                </th>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('admin.table.status')}
                </th>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('admin.table.views')}
                </th>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('admin.table.date')}
                </th>
                <th className={`px-6 py-3 ${isRTL ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('admin.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900 line-clamp-1">
                        {post.title}
                      </div>
                      <div className="text-sm text-gray-500 line-clamp-1">
                        /blog/{post.slug}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                      {t(`admin.categories.${post.category}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        post.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : post.status === 'archived'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {t(`admin.status.${post.status}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {post.view_count}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {post.published_at
                      ? new Date(post.published_at).toLocaleDateString()
                      : new Date(post.created_at).toLocaleDateString()}
                  </td>
                  <td className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'}`}>
                    <div className={`flex items-center ${isRTL ? 'justify-start' : 'justify-end'} gap-2`}>
                      {post.status === 'published' && (
                        <a
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title={t('admin.actions.view')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </a>
                      )}
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title={t('admin.actions.edit')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(post.slug)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title={t('admin.actions.delete')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('admin.pagination.previous')}
              </button>
              <span className="text-sm text-gray-500">
                {t('admin.pagination.pageOf', { page: pagination.page, total: pagination.totalPages })}
              </span>
              <button
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('admin.pagination.next')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
